// Bidirectional sync between the canonical active-investigation id (in
// `investigations-store`) and the `?investigation=<id>` URL search param.
//
// Mount this in any investigate page (graph, map, timeline, time-axis) so
// picking an investigation produces a shareable / refreshable URL, and so
// arriving on a page with `?investigation=<id>` activates the matching
// investigation once it has loaded.
//
// - URL → store: when the URL carries an id and the store hasn't activated
//   it yet, call `setActive` once the saved list contains a match.
// - Store → URL: when `activeId` becomes non-null, write the param. We only
//   STRIP the param after we've actually seen an active id in this hook
//   instance — otherwise an initial mount (before `fetchAll` resolves) would
//   wipe the URL and the deep link would be lost on every reload.

import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'

const PARAM = 'investigation'

export function useInvestigationUrlSync(): void {
  const [searchParams, setSearchParams] = useSearchParams()
  const storeApi = useInvestigationsStoreApi()
  const activeId = useInvestigationsStore(s => s.activeId)
  const saved = useInvestigationsStore(s => s.saved)
  const urlId = searchParams.get(PARAM)
  const hadActiveRef = useRef(false)

  // URL → store. Activates the URL-specified investigation as soon as it
  // appears in the saved list (handles initial load races where the store's
  // mount-time `fetchAll` hasn't resolved yet).
  useEffect(() => {
    if (!urlId) return
    if (activeId === urlId) return
    const target = saved.find(inv => inv.id === urlId)
    if (!target) return
    storeApi.getState().setActive(target.id)
  }, [urlId, activeId, saved, storeApi])

  // Store → URL. Writes the param when something is active; clears it only
  // after we've actually seen an active id (so the initial-load activeId=null
  // doesn't wipe the deep-link before `URL → store` gets a chance to run).
  useEffect(() => {
    const current = new URLSearchParams(searchParams)
    if (activeId) {
      hadActiveRef.current = true
      if (current.get(PARAM) === activeId) return
      current.set(PARAM, activeId)
      setSearchParams(current, { replace: true })
      return
    }
    if (!hadActiveRef.current) return
    if (!current.has(PARAM)) return
    current.delete(PARAM)
    setSearchParams(current, { replace: true })
  }, [activeId, searchParams, setSearchParams])
}
